import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Save, Cancel, Edit } from "@mui/icons-material";
import { toTitleCase } from "../../../utils/toTitleCase";
import { camelCaseToWords } from "../../../utils/camelCaseToWords";
import useConfirmDialog from "../../../utils/useConfirmDialogue";
import _ from "lodash";
import useRequestSuccessEffect from "../../../utils/useRequestSuccessEffect";
import biosenseAPI from "../../../api/biosense";
import SwalMediaImageUpload from "../../components/swalMediaImageUpload";
import base64ToImg from "../../../utils/base64ToImg";
import dayjs from "dayjs";
import useFeedbackDialog from "../../../utils/useFeedbackDialogue";

import DeviceControlComponent from "../mainComponents/device/deviceControl";
import { useTranslation } from "react-i18next";

const DeviceDetailsScreen = ({ deviceDetailsProps }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState(null);
  const [editMode, setEditMode] = useState({ field: null, value: null });
  const [requestSuccess, setRequestSuccess] = useRequestSuccessEffect(() => {
    biosenseAPI.getDeviceDetails({ sn: deviceDetailsProps.device.sn }).then((res) => {
      setDevice(res.device);
      setLoading(false);
    });
  });
  const isDev = process.env.NODE_ENV === "development";

  const confirmAction = useConfirmDialog();
  const { showSuccess, showError } = useFeedbackDialog();

  // Load device data
  useEffect(() => {
    const fetchDeviceDetails = async () => {
      try {
        const response = await biosenseAPI.getDeviceDetails({ sn: deviceDetailsProps.device.sn });
        setDevice(response.device);
      } catch (error) {
        showError("Load Failed", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDeviceDetails();
  }, []);

  // Handle edit mode toggle
  const handleEditToggle = (fieldName) => {
    if (editMode.field === fieldName) {
      // Check for changes
      if (_.isEqual(editMode.value, device[fieldName])) {
        setEditMode({ field: null, value: null });
        return;
      }

      // Confirm save changes
      confirmAction(
        {
          title: "Save Changes?",
          text: `Are you sure you want to save changes to ${camelCaseToWords(fieldName)}?`,
          icon: "question",
        },
        () => saveFieldChange(fieldName, editMode.value),
      );
    } else {
      // Enter edit mode
      setEditMode({ field: fieldName, value: device[fieldName] });
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (_.isEqual(editMode.value, device[editMode.field])) {
      setEditMode({ field: null, value: null });
      return;
    }

    confirmAction(
      {
        title: "Discard Changes?",
        text: "Your changes will not be saved.",
        icon: "warning",
      },
      () => setEditMode({ field: null, value: null }),
    );
  };

  // Save field changes
  const saveFieldChange = async (fieldName, value) => {
    try {
      // Other fields update directly
      await biosenseAPI.updateDeviceField({
        sn: device.sn,
        field: fieldName,
        value: value,
      });

      // Refresh device data after successful update
      const response = await biosenseAPI.getDeviceDetails({ sn: device.sn });
      setDevice(response.device);
      showSuccess("Update Successful", `${camelCaseToWords(fieldName)} has been updated`);

      // Reset edit state
      setEditMode({ field: null, value: null });
    } catch (error) {
      showError("Update Failed", error.message);
    }
  };

  const handleImageUpload = (file) => {
    return new Promise((resolve, reject) => {
      // 创建图片对象
      const img = new Image();

      // 创建FileReader读取文件
      const reader = new FileReader();

      // 读取完成后处理图片
      reader.onload = (e) => {
        img.src = e.target.result;
      };

      // 图片加载完成后进行转换
      img.onload = async () => {
        // 创建Canvas元素
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // 设置Canvas尺寸与图片相同
        canvas.width = img.width;
        canvas.height = img.height;

        // 将图片绘制到Canvas
        ctx.drawImage(img, 0, 0);

        // 将Canvas内容转换为PNG格式的Base64字符串
        const base64Data = canvas.toDataURL("image/png");

        // 移除data:image/png;base64,前缀，只保留编码部分
        const base64Image = base64Data.split(",")[1];

        try {
          // 调用API上传Base64数据
          await biosenseAPI.updateDeviceImage({ base64Image, sn: device.sn });

          // 更新设备详情
          const response = await biosenseAPI.getDeviceDetails({ sn: device.sn });
          setDevice(response.device);

          resolve(() => {
            return true;
          });
        } catch (error) {
          reject(error);
        }
      };

      // 错误处理
      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      // 开始读取文件
      reader.readAsDataURL(file);
    });
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ height: "100%", p: 1 }}>
        {/* 使用CSS Grid实现等高布局 */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1,
            height: "100%",
            // md断点以上使用两列布局
            "@media (min-width: 900px)": {
              gridTemplateColumns: "5fr 7fr",
            },
          }}
        >
          {/* 左侧卡片 - 设备信息 */}
          <Box sx={{ height: "100%" }}>
            <Card sx={{ height: "100%", boxShadow: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ width: 80, height: 80 }}>
                    <SwalMediaImageUpload
                      props={{
                        component: "img",
                        isWithBlur: false,
                        title: "(沒有名字)",
                        height: "150",
                        width: "150",
                        style: {
                          objectFit: "cover",
                          objectPosition: "0 -50px",
                          borderRadius: "4px 4px 0 0",
                        },
                        src: "",
                        alt: "Card Background",
                      }}
                    />
                  </Avatar>
                  <Typography variant='h6' gutterBottom>
                    <CircularProgress sx={{ mt: 1 }} />
                  </Typography>
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                    {t("sn")}: <CircularProgress sx={{ mt: 1 }} />
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <List sx={{ p: 0 }}>
                  {["Series", "Status", "Settings Last Updated", "Device Created At"].map(
                    (fieldName) => (
                      <ListItem key={fieldName} sx={{ py: 1 }}>
                        <ListItemText
                          primary={fieldName}
                          secondary={<CircularProgress sx={{ mt: 1 }} />}
                        />
                      </ListItem>
                    ),
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>

          {/* 右侧卡片 - 设备详细信息 */}
          <Box sx={{ height: "100%" }}>
            <Card sx={{ height: "100%", boxShadow: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant={"h6"} sx={{ mb: 1 }}>
                  {t("device.details_deviceinformation")}
                </Typography>
                <List sx={{ p: 0 }}>
                  {["alias", "city", "country", "district", "lat", "lng"].map((fieldName) => (
                    <ListItem key={fieldName} sx={{ py: 1 }}>
                      <Grid container alignItems='center'>
                        <Grid item xs={8}>
                          <ListItemText
                            sx={{ width: { lg: "14em" } }}
                            primary={toTitleCase(camelCaseToWords(fieldName))}
                            secondary={<CircularProgress sx={{ mt: 1 }} />}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sx={{ display: "flex", justifyContent: "flex-end", columnGap: 1 }}
                        >
                          <CircularProgress sx={{ mt: 1 }} />
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* 设备控制组件 */}
        <Box sx={{ mt: 1 }}>
          <DeviceControlComponent device={device} />
        </Box>
      </Box>
    );
  }

  // Render field item
  const renderFieldItem = (fieldName) => {
    const fieldLabelMapping = {
      alias: t("alias"),
      city: t("city"),
      country: t("country"),
      district: t("district"),
      lat: t("lat"),
      lng: t("lng"),
    };

    const label = fieldLabelMapping[fieldName];
    const isEditing = editMode.field === fieldName;

    return (
      <ListItem key={fieldName} sx={{ py: 1 }}>
        <Grid container alignItems='center'>
          <Grid item xs={8}>
            <Box display='flex' gap={2} alignItems={"center"}>
              <ListItemText
                sx={{ width: { lg: "14em" } }}
                primary={label}
                secondary={isEditing ? "" : device[fieldName]}
              />
              {isEditing && (
                <TextField
                  margin='dense'
                  required
                  fullWidth
                  id={fieldName}
                  name={fieldName}
                  autoComplete={fieldName}
                  autoFocus
                  value={editMode.value}
                  onChange={(e) => setEditMode({ ...editMode, value: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title={t("cancel")}>
                          <IconButton size='small' color='error' onClick={handleCancelEdit}>
                            <Cancel fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("save")}>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={() => handleEditToggle(fieldName)}
                          >
                            <Save fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ),
                  }}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={4} sx={{ display: "flex", justifyContent: "flex-end", columnGap: 1 }}>
            {!isEditing && (
              <Button
                variant='outlined'
                color='primary'
                onClick={() => handleEditToggle(fieldName)}
                size='small'
                startIcon={<Edit fontSize='small' />}
              >
                {t("edit")}
              </Button>
            )}
          </Grid>
        </Grid>
      </ListItem>
    );
  };

  return (
    <Box sx={{ height: "100%", p: 1 }}>
      {/* 使用CSS Grid实现等高布局 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 1,
          height: "100%",
          // md断点以上使用两列布局
          "@media (min-width: 900px)": {
            gridTemplateColumns: "5fr 7fr",
          },
        }}
      >
        {/* 左侧卡片 - 设备信息 */}
        <Box sx={{ height: "100%" }}>
          <Card sx={{ height: "100%", boxShadow: 2, overflow: "hidden" }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ width: 80, height: 80 }}>
                  <SwalMediaImageUpload
                    props={{
                      component: "img",
                      isWithBlur: false,
                      title: device.alias ?? "(沒有名字)",
                      height: "150",
                      width: "150",
                      style: {
                        objectFit: "cover",
                        objectPosition: "0 -50px",
                        borderRadius: "4px 4px 0 0",
                      },
                      src: base64ToImg({ base64String: device.img }),
                      alt: "Card Background",
                    }}
                    onUpload={(file) => handleImageUpload(file)}
                  />
                </Avatar>
                <Typography variant='h6' gutterBottom>
                  {device.alias}
                </Typography>
                <Typography variant='body2' color='text.secondary' gutterBottom>
                  {t("sn")}: {device.sn}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <List sx={{ p: 0 }}>
                <ListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={t("series")}
                    secondary={`${device.series} ${device?.seriesAbbr}`}
                  />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <ListItemText primary={t("status")} secondary={device.status} />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={t("settingslastupdated")}
                    secondary={dayjs(device.updated_at).format("YYYY-MM-DD HH:mm:ss")}
                  />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={t("devicecreatedat")}
                    secondary={dayjs(device.created_at).format("YYYY-MM-DD HH:mm:ss")}
                  />
                </ListItem>
              </List>
              {isDev && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button variant='contained' color='primary' size='small'>
                    {t("device.details_editdevice")}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* 右侧卡片 - 设备详细信息 */}
        <Box sx={{ height: "100%" }}>
          <Card sx={{ height: "100%", boxShadow: 2, overflow: "hidden" }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant={"h6"} sx={{ mb: 1 }}>
                {t("device.details_deviceinformation")}
              </Typography>
              <List sx={{ p: 0 }}>
                {["alias", "city", "country", "district", "lat", "lng"].map((fieldName) =>
                  renderFieldItem(fieldName),
                )}
              </List>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* 设备控制组件 */}
      <Box sx={{ mt: 1 }}>
        <DeviceControlComponent device={device} />
      </Box>
    </Box>
  );
};

export default DeviceDetailsScreen;
